import type { ComponentType } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { ArrayField } from "@/widgets/classic-form/form";
import type { FieldMapperProps } from "@/features/fields-grid";
import { useAddFieldTrigger, useRemoveFieldTrigger } from "@/features/form-fields";
import { getEventModelName } from "@/entities/data-model";
import type { EventFieldsGroup, EventFieldset } from "@/entities/form-metadata";
import type { EventAssertion } from "@/entities/form-metadata";
import { type FieldPath, useFormAPI } from "@/shared/forms";
import { AddNewEventFieldPanel } from "../Panel/AddNewEventFieldPanel";
import { EventInstanceRow } from "./EventInstanceRow";
import type { YearRangeGridConfig } from "./gridLayout";
import styles from "./YearRangeEventGroup.module.scss";

interface YearRangeEventGroupProps {
    fieldGroup: EventFieldsGroup;
    FieldMapper: ComponentType<FieldMapperProps<EventAssertion>>;
    gridConfig: YearRangeGridConfig;
}

export const YearRangeEventGroup: React.FC<YearRangeEventGroupProps> = ({ fieldGroup, FieldMapper, gridConfig }) => {
    const { fieldsets } = fieldGroup;

    const onRemoveItem = useRemoveFieldTrigger();
    const addNew = useAddFieldTrigger();
    const { getFieldValue } = useFormAPI();

    const handleItemAdded = (fieldset: EventFieldset) => {
        const eventKey = getEventModelName(fieldset);
        const instancesPath = `events.${eventKey}.instances` as FieldPath<FormValues>;
        const instances = (getFieldValue(instancesPath) as Array<unknown>) || [];
        const newItemIndex = instances.length;

        addNew({
            fieldName: instancesPath,
            type: "event",
            newItemIndex,
            mappedAssertion: "date.year",
        });
    };

    return (
        <div className={styles.root}>
            {gridConfig.Removable && <AddNewEventFieldPanel fieldsets={fieldsets} onItemAdded={handleItemAdded} />}

            <ul className="listRuled">
                {fieldsets.map((fieldset) => {
                    const eventKey = getEventModelName(fieldset);
                    const arrayPath = `events.${eventKey}.instances` as FieldPath<FormValues>;

                    return (
                        <ArrayField key={arrayPath} name={arrayPath}>
                            {({ items }) =>
                                items?.map((_, index) => {
                                    const fieldPath = `events.${eventKey}.instances.${index}`;
                                    return (
                                        <li className={styles.listItem} key={`${eventKey}-${index}`}>
                                            <EventInstanceRow
                                                instanceIndex={index}
                                                fieldset={fieldset}
                                                fieldPath={fieldPath}
                                                onRemove={() => onRemoveItem({ fieldName: arrayPath, index })}
                                                FieldMapper={FieldMapper}
                                                gridConfig={gridConfig}
                                            />
                                        </li>
                                    );
                                })
                            }
                        </ArrayField>
                    );
                })}
            </ul>
        </div>
    );
};
